import { z } from 'zod';

import {
  ACTION_TYPES,
  ACTION_TYPES_WITH_EXECUTOR,
  boundedConditionGroupSchema,
  eventTriggerSchema,
  metricTriggerSchema,
  scheduleTriggerSchema,
  workflowActionSchema,
  workflowScopeSchema,
  type ActionType,
  type WorkflowTrigger,
} from './automation.js';

/**
 * AUTOMATION GRAPH — akış motorunun SÖZLEŞMESİ (schemaVersion 2).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden ikinci bir sözleşme, `automation.ts`i genişletmek yerine
 * ═══════════════════════════════════════════════════════════════════════════
 * Bugünkü kural DOĞRUSAL ve şekli bunu söylüyor: bir tetikleyici → bir koşul
 * ağacı → bir kapsam → sıralı aksiyon listesi (`workflowUpsertSchema`). O
 * şeklin taşıyamadığı üç şey var ve üçü de ürünün istediği şey:
 *
 *   1. DALLANMA — "ROAS iyiyse bütçeyi artır, kötüyse duraklat". Bugün bu iki
 *      ayrı kural olmak zorunda ve ikisi ayrı ayrı bakım istiyor.
 *   2. AKSİYON BAŞINA KOŞUL — dizideki her aksiyon aynı koşulu paylaşıyor.
 *   3. BEKLEME — "duraklat, 24 saat sonra tekrar bak" yazılamıyor.
 *
 * `workflowUpsertSchema`ya `branch` eklemek, alanlarının yarısı yalnız belirli
 * bir düzende anlamlı olan bir şema üretirdi (`video.ts`/`studio.ts`
 * ayrımıyla aynı gerekçe). Grafik AYRI bir belge; `schemaVersion` alanı zaten
 * var ve `1` ile `2` yan yana yaşayabiliyor.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Düğüm `data` şekilleri MEVCUT ŞEMALARIN KENDİSİ
 * ---------------------------------------------------------------------------
 * `scheduleTriggerSchema`, `metricTriggerSchema`, `eventTriggerSchema`,
 * `workflowScopeSchema`, `conditionGroupSchema` ve `workflowActionSchema`
 * BURADAN İMPORT EDİLİYOR, kopyalanmıyor. Sadeleştirilmiş bir kopya, editörde
 * geçerli görünen bir grafiğin kaydetmede reddedilmesi demek olurdu — bu
 * deponun `compose.service.ts` başlığında yazılı ders, aynısı burada da geçerli.
 *
 * Pratik sonucu şu: bir gün `budget_adjust`a yeni bir alan eklenirse grafik
 * düğümü onu KENDİLİĞİNDEN alır. İki şema arasında senkron tutulacak hiçbir
 * şey yok çünkü ikinci bir şema yok.
 *
 * ---------------------------------------------------------------------------
 * Bu dosya NE DEĞİL
 * ---------------------------------------------------------------------------
 * Motor değil, editör değil, v1 → v2 dönüştürücüsü değil. Yalnız şekil,
 * sınırlar ve şekil doğrulaması. Motorun yürütme sırası `graphTopologicalOrder`
 * ile buradan okunuyor ama YÜRÜTME burada yaşamıyor.
 */

export const GRAPH_SCHEMA_VERSION = 2;

// ═══════════════════════════════════════════════════════════════════════════
// Sert sınırlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 EDİTÖR VE SUNUCU BU SABİTLERİ OKUR — ikinci bir sayı yazılmaz.
 *
 * `MAX_CONDITION_DEPTH`/`MAX_CONDITION_RULES` ile aynı fikir: sınırın iki
 * yerde ayrı yazılması, editörde kabul edilip kaydetmede reddedilen (ya da
 * tersi) bir grafik demek. Kullanıcı hangisinin doğru olduğunu çıkaramaz.
 */

/// Tuvaldeki toplam düğüm. 40, "bir ekranda okunabilir" sınırı: bunun
/// üstündeki bir akışı kimse gözle takip edemez ve takip edilemeyen bir akış
/// para hareket ettiriyorsa sınır bir zevk meselesi değil.
export const MAX_GRAPH_NODES = 40;

/**
 * En uzun yolun DÜĞÜM sayısı (tetikleyici dahil, 1'den sayılır).
 *
 * 🔴 13 ve bu sayı `MAX_ACTIONS_PER_GRAPH`TEN TÜREDİ, zevkten değil.
 *
 * Önce 12'ydi ("tetikleyici → kapsam → üç seviyeli dallanma → bekleme →
 * aksiyon zinciri rahat sığıyor") ve o hâliyle sözleşme KENDİ İÇİNDE
 * ÇELİŞİYORDU: `graphFromLegacy` doğrusal kuralı `trigger → scope → filter →
 * aksiyon zinciri` diye açıyor, yani derinlik `3 + aksiyon sayısı`.
 * `workflowActionsSchema` 10 aksiyona izin verdiği için 10 aksiyonlu bir v1
 * kural derinlik 13 üretiyor ve `GRAPH_TOO_DEEP` yiyordu — motor yalnız grafik
 * yürüttüğü için o kural HİÇ ÇALIŞAMAZDI ve kullanıcı çalıştığını sanırdı
 * (ölçüldü: 9 aksiyon derinlik 12, temiz; 10 aksiyon derinlik 13, geçersiz).
 *
 * İki çıkış yolu vardı ve diğeri reddedildi: `workflowActionsSchema`yı 9'a
 * çekmek BUGÜN KAYITLI kuralları geçersiz kılardı — sözleşmeyi daraltmak
 * veriyi geçmişe dönük bozmak demek. Tavanı 13'e çıkarmak hiçbir kuralı
 * bozmuyor ve okunabilirlik gerekçesini de bozmuyor: 13, "en uzun v1 kuralı
 * tam olarak sığar" demek, keyfî bir genişletme değil.
 *
 * İki sabit BİRLİKTE değişir: `MAX_ACTIONS_PER_GRAPH` artarsa burası da artar.
 */
export const MAX_GRAPH_DEPTH = 13;

/**
 * 🔴 `workflowActionsSchema.max(10)` İLE AYNI SAYI.
 *
 * Doğrusal kuralda tavan 10 aksiyondu; grafikte aksiyon düğümü sayısı aynı
 * tavana tabi. Ayrı bir sayı vermek, aynı kuralın v1'den v2'ye geçince
 * sessizce daha fazla para hareket ettirebilmesi demekti — geçişin kendisi
 * bir güvenlik gevşemesi olurdu.
 *
 * ⚠️ Zod'un `max(10)`u `automation.ts`te bir sayı değişmezi ve oradan
 * okunamıyor. İki sayı DEĞİŞİRSE beraber değişmeli — ve `MAX_GRAPH_DEPTH`
 * (= 3 + bu sayı) de onlarla birlikte.
 */
export const MAX_ACTIONS_PER_GRAPH = 10;

/**
 * Kenar tavanı.
 *
 * Fan-in yasak olduğu için (bkz. `GRAPH_NODE_MULTIPLE_INPUTS`) geçerli bir
 * grafikte kenar sayısı en fazla düğüm sayısı kadardır. Buradaki tavan bir
 * KURAL değil bir YÜK sınırı: şekil denetleyicisi çalışmadan önce gövdenin
 * boyutunu bağlamak gerekiyor, yoksa 40 düğümlük bir grafik 100.000 kenarla
 * gelip denetleyiciyi meşgul edebilir.
 */
export const MAX_GRAPH_EDGES = MAX_GRAPH_NODES;

/**
 * `wait` düğümünün en uzun beklemesi — 7 gün.
 *
 * Üstü anlamsız: bekleme biterken kararın dayandığı metrikler bir haftadan
 * eski olur ve akış, kullanıcının unuttuğu bir şeyi uygular. Ayrıca bekleyen
 * her akış motorda açık bir iş demek; süreyi bağlamak o kuyruğu da bağlıyor.
 */
export const MAX_WAIT_MINUTES = 10_080;

/**
 * Bir KOŞUNUN toplam ömrü — 8 gün.
 *
 * 🔴 `MAX_WAIT_MINUTES`in kopyası değil, ONUN ÜSTÜNE KONAN İKİNCİ KEMER.
 *
 * Bekleme süresi grafikten geliyor ve yol başına tek `wait` var
 * (`GRAPH_WAIT_MULTIPLE_ON_PATH`), yani teorik tavan zaten 7 gün. Ama uyanma
 * anını grafik DEĞİL kuyruk belirliyor: gecikmiş bir iş, durdurulup günler
 * sonra ayağa kalkan bir worker ya da geriye alınmış bir sistem saati, 7 günü
 * çoktan geçmiş bir koşuyu uyandırabilir. O koşu, kararının dayandığı
 * metrikler bir haftadan eskiyken para hareket ettirir — kullanıcının
 * unuttuğu bir şeyi uygular.
 *
 * Bu yüzden devam eden taraf `startedAt`e bakıp bu tavanı aşan koşuyu
 * ÇALIŞTIRMADAN kapatıyor. 8 gün = 7 günlük tavan + 1 günlük kuyruk payı:
 * pay olmadan, gecikmiş ama meşru bir uyanma (worker yeniden başladı) da
 * atılırdı.
 */
export const MAX_RUN_LIFETIME_MINUTES = MAX_WAIT_MINUTES + 1_440;

// ═══════════════════════════════════════════════════════════════════════════
// Düğüm cinsleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tetikleyiciler.
 *
 * İlk üçü bugün `workflowTriggerSchema`da da var ve şemaları AYNI. Son ikisi
 * yalnız grafikte yaşıyor ve bu bilinçli: doğrusal kurala `webhook` eklemek,
 * o kuralın kapsamını dışarıdan gelen bir çağrının belirlemesi demekti ve
 * doğrusal şeklin bunu ifade edecek yeri yok.
 */
export const GRAPH_TRIGGER_KINDS = [
  'trigger.schedule',
  'trigger.metric',
  'trigger.event',
  /// Faz 5 — dış sistem çağırır.
  'trigger.webhook',
  /// "Şimdi çalıştır" düğmesi.
  'trigger.manual',
] as const;
export type GraphTriggerKind = (typeof GRAPH_TRIGGER_KINDS)[number];

/**
 * Akış düğümleri — hedef kümesini üreten, daraltan, bölen ve bekleten cinsler.
 *
 * `filter` ile `branch` AYNI koşul ağacını taşıyor ve fark tek bir şeyde:
 * `filter` yalnız GEÇENLERİ tek çıkıştan devam ettiriyor, `branch` kümeyi
 * İKİYE bölüp iki ayrı porttan gönderiyor. İki ayrı cins olmalarının sebebi
 * editörün çizdiği şeklin farklı olması değil, KAYBOLAN VERİNİN farklı
 * olması: `filter`da geçmeyen hedefler akıştan düşüyor ve bu bilgi bir daha
 * kullanılamıyor; `branch`te düşen hiçbir şey yok. Tek bir cinse "ikinci
 * portu bağlarsan branch olur" dedirtmek, aynı düğümün bağlantı durumuna göre
 * anlam değiştirmesi olurdu.
 */
export const GRAPH_FLOW_KINDS = ['scope', 'filter', 'branch', 'wait'] as const;
export type GraphFlowKind = (typeof GRAPH_FLOW_KINDS)[number];

/// `action.pause`, `action.budget_adjust`, … — 11 mevcut aksiyon tipinin
/// her biri için bir düğüm cinsi. Liste `ACTION_TYPES`ten TÜRETİLİYOR;
/// elle yazılsaydı yeni bir aksiyon eklendiğinde burada eksik kalırdı.
export type GraphActionKind = `action.${ActionType}`;
export const GRAPH_ACTION_KINDS: readonly GraphActionKind[] = ACTION_TYPES.map(
  (type): GraphActionKind => `action.${type}`,
);

/// Faz 5 — dış çağrı. `ACTION_TYPES` içinde DEĞİL ve olmamalı: doğrusal kural
/// bunu çalıştıramaz (guardrail'i yok, geri alınamaz, hedefe yazmıyor) ve
/// `ACTION_TYPES`e eklemek onu `workflowActionSchema` üzerinden v1 kurallara
/// da açardı.
export const GRAPH_HTTP_ACTION_KIND = 'action.http' as const;

export type GraphNodeKind =
  | GraphTriggerKind
  | GraphFlowKind
  | GraphActionKind
  | typeof GRAPH_HTTP_ACTION_KIND;

export const GRAPH_NODE_KINDS: readonly GraphNodeKind[] = [
  ...GRAPH_TRIGGER_KINDS,
  ...GRAPH_FLOW_KINDS,
  ...GRAPH_ACTION_KINDS,
  GRAPH_HTTP_ACTION_KIND,
];

/**
 * Hedef kümesini TÜKETEN cinsler — yani `scope` düğümünün AŞAĞISINDA olmak
 * zorunda olanlar.
 *
 * `scope` kümeyi üretiyor; bu listedekiler onu okuyor. Kapsamdan önce
 * yerleştirilmiş bir `filter` ya da aksiyon, boş kümeyle çalışır ve hiçbir şey
 * yapmaz — sessizce. Şekil denetleyicisi bunu `GRAPH_NODE_BEFORE_SCOPE` ile
 * söylüyor.
 */
export const GRAPH_TARGET_CONSUMER_KINDS: readonly GraphNodeKind[] = [
  'filter',
  'branch',
  'wait',
  ...GRAPH_ACTION_KINDS,
  GRAPH_HTTP_ACTION_KIND,
];

const TRIGGER_KIND_SET: ReadonlySet<string> = new Set(GRAPH_TRIGGER_KINDS);
const ACTION_KIND_SET: ReadonlySet<string> = new Set<string>([
  ...GRAPH_ACTION_KINDS,
  GRAPH_HTTP_ACTION_KIND,
]);
const CONSUMER_KIND_SET: ReadonlySet<string> = new Set<string>(GRAPH_TARGET_CONSUMER_KINDS);

export function isGraphTriggerKind(kind: string): kind is GraphTriggerKind {
  return TRIGGER_KIND_SET.has(kind);
}

/// `action.http` DAHİL — aksiyon tavanı onu da sayıyor. Dış çağrı da bir
/// yan etki ve tavanın amacı yan etki sayısını bağlamak.
export function isGraphActionKind(kind: string): boolean {
  return ACTION_KIND_SET.has(kind);
}

/**
 * `action.pause` → `'pause'`. `action.http` ve aksiyon olmayan cinsler için
 * `null`.
 *
 * Dönüştürücüler ve motor bunu okuyor: düğüm cinsinden `ACTION_TYPES`
 * sözlüğüne (uygulayıcı tablosu, i18n anahtarı, guardrail eşlemesi) geçiş tek
 * yerden yapılsın. Dize kesme (`kind.slice(7)`) çağıran tarafta tekrarlansaydı
 * `action.http` sessizce `'http'` diye bir `ActionType` uydururdu.
 */
export function graphActionType(kind: string): ActionType | null {
  if (!kind.startsWith('action.')) return null;
  const type = kind.slice('action.'.length);
  return (ACTION_TYPES as readonly string[]).includes(type) ? (type as ActionType) : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Portlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir düğümün çıkış portları.
 *
 * `branch` dışındaki HER cins tek porttan (`out`) çıkıyor; `branch` `true` ve
 * `false` portlarından. Port adının kenarda taşınmasının sebebi, aynı
 * `branch`ten çıkan iki kenarın hangi dalı temsil ettiğinin başka türlü
 * bilinememesi — kenar sırasına güvenmek, JSON dizisinin sırasına anlam
 * yüklemek olurdu ve o sıra editörde sürükleme ile değişir.
 */
export const GRAPH_PORTS = ['out', 'true', 'false'] as const;
export type GraphPort = (typeof GRAPH_PORTS)[number];
export const graphPortSchema = z.enum(GRAPH_PORTS);

// ═══════════════════════════════════════════════════════════════════════════
// Yalnız grafikte yaşayan düğüm verileri
// ═══════════════════════════════════════════════════════════════════════════

/// `scheduleTriggerSchema` ile aynı biçim — saat `HH:mm`, workspace zaman
/// diliminde yorumlanır.
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * KE-G1 — WEBHOOK TETİKLEYİCİSİ (Faz 5).
 *
 * ---------------------------------------------------------------------------
 * 🔴 SIR BURADA DURMAZ
 * ---------------------------------------------------------------------------
 * İmza anahtarı bu şemada YOK ve olmayacak. Grafik belgesi; şablona
 * dönüştürülüyor, dışa aktarılıyor, `compose` ucunda modele metin olarak
 * giriyor ve workspace'teki her üyeye okunabiliyor. Bir HMAC anahtarının bu
 * yolların hiçbirinde işi yok. Anahtar ayrı bir satırda (`TOKEN_VAULT_KEY` ile
 * şifreli) yaşıyor, grafik yalnız `path` ile ona işaret ediyor.
 *
 * `signature` varsayılanı `hmac_sha256`: imzasız bir webhook, URL'i bilen
 * herkesin kuralı çalıştırabilmesi demek ve o kural para hareket ettirebiliyor.
 * `none` seçilebilir ama SEÇİLMESİ gerekiyor.
 *
 * `cooldownMinutes` varsayılanı 0 — `eventTriggerSchema`nın 60'ının AKSİNE.
 * Olay yayıncısı bizim tarafımız ve gürültüyü biz üretiyoruz; webhook'u çağıran
 * dış sistem ne zaman çağıracağına kendi karar vermiş oluyor. Varsayılan bir
 * susturma, çağıranın başarılı sandığı isteği sessizce düşürürdü.
 */
export const webhookTriggerSchema = z.object({
  kind: z.literal('webhook'),
  /// URL soneki: `POST /automations/hooks/<workspaceId>/<path>`. Küçük harf ve
  /// tire — yol parçası olduğu için kaçış istemeyen bir alfabe.
  path: z
    .string()
    .trim()
    .min(3)
    .max(60)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'Yol küçük harf, rakam ve tire içerebilir'),
  signature: z.enum(['none', 'hmac_sha256']).default('hmac_sha256'),
  cooldownMinutes: z.number().int().min(0).max(1440).default(0),
});
export type WebhookTrigger = z.infer<typeof webhookTriggerSchema>;

/**
 * KE-G2 — ELLE TETİKLEME.
 *
 * Alan taşımıyor ve taşımamalı: "şimdi çalıştır" düğmesinin parametresi yok.
 * Ayrı bir cins olmasının sebebi, elle çalıştırılabilen bir akışın
 * zamanlayıcıda YERİ OLMAMASI — `isScheduledTrigger` mantığının grafikteki
 * karşılığı. Zamanlama alanı olmayan bir `schedule` tetikleyicisi ile ifade
 * edilseydi, zamanlayıcı onu her turda seçip hiçbir şey yapmadan bırakırdı.
 */
export const manualTriggerSchema = z.object({
  kind: z.literal('manual'),
});
export type ManualTrigger = z.infer<typeof manualTriggerSchema>;

/// Grafikte kullanılabilen TÜM tetikleyiciler. `workflowTriggerSchema`nın
/// üstüne iki cins ekliyor; o şemaya dokunmuyor çünkü doğrusal kural bu ikisini
/// çalıştıramaz.
export const graphTriggerSchema = z.discriminatedUnion('kind', [
  scheduleTriggerSchema,
  metricTriggerSchema,
  eventTriggerSchema,
  webhookTriggerSchema,
  manualTriggerSchema,
]);
export type GraphTrigger = WorkflowTrigger | WebhookTrigger | ManualTrigger;

/**
 * BEKLEME DÜĞÜMÜ (Faz 3).
 *
 * İki kip ayrı şemalar çünkü alanları ortak değil: süre bekleyen bir düğümün
 * saati yok, saate bekleyenin dakikası. Tek bir nesnede iki opsiyonel alanla
 * ifade edilseydi "ikisi de dolu" ve "ikisi de boş" durumları şema seviyesinde
 * geçerli görünürdü.
 *
 * 🔴 `until_time` workspace ZAMAN DİLİMİNDE yorumlanır — `scheduleTriggerSchema`
 * ile aynı kural. `dayOffset` 0 iken saat GEÇMİŞSE motor ertesi güne alır;
 * geçmiş bir anı beklemek, hiç beklememekle aynı şey olurdu ve kullanıcı
 * beklemediği bir anda uygulanan bir aksiyon görürdü.
 */
export const waitDurationSchema = z.object({
  mode: z.literal('duration'),
  minutes: z.number().int().min(1).max(MAX_WAIT_MINUTES).default(60),
});

export const waitUntilTimeSchema = z.object({
  mode: z.literal('until_time'),
  atTime: z.string().regex(HHMM, 'Saat HH:mm biçiminde olmalı'),
  /// Kaç gün sonrasının o saati. 0 = bugün (saat geçtiyse yarın).
  dayOffset: z.number().int().min(0).max(7).default(0),
});

export const waitNodeSchema = z.discriminatedUnion('mode', [
  waitDurationSchema,
  waitUntilTimeSchema,
]);
export type WaitNodeData = z.infer<typeof waitNodeSchema>;

/**
 * DIŞ ÇAĞRI AKSİYONU (Faz 5).
 *
 * ---------------------------------------------------------------------------
 * 🔴 KİMLİK BİLGİSİ BAŞLIKTA TAŞINMAZ
 * ---------------------------------------------------------------------------
 * `Authorization`, `Cookie` ve `Proxy-Authorization` başlıkları REDDEDİLİYOR.
 * Sebebi webhook sırrıyla aynı: grafik belgesi dışa aktarılabiliyor, şablona
 * dönüşebiliyor ve modele metin olarak giriyor. Bir API anahtarının bu
 * yollarda dolaşması, anahtarın sızması demek. Kimlik bilgisi ayrı bir satırda
 * yaşıyor ve grafik ona `credentialId` ile işaret ediyor.
 *
 * ---------------------------------------------------------------------------
 * 🔴 SSRF — `https` ZORUNLU, ÖZEL AĞ KAPALI
 * ---------------------------------------------------------------------------
 * Bu çağrıyı SUNUCUMUZ yapıyor. `http://169.254.169.254/` yazan bir kullanıcı,
 * bizim bulut kimlik uçlarımızı okumaya çalışıyor demektir; `http://127.0.0.1:3040`
 * yazan kendi kiracılığının dışına çıkmaya. `isBlockedHttpHost` bunu şema
 * seviyesinde kesiyor.
 *
 * ⚠️ Şema denetimi YETMEZ ve yeterli sanılmamalı: saldırgan kendi alan adını
 * `127.0.0.1`e çözdürebilir (DNS rebinding). Motor isteği atmadan ÖNCE çözülen
 * IP'yi aynı fonksiyondan geçirmek zorunda. Bu yüzden `isBlockedHttpHost`
 * dışa açık — iki taraf aynı listeyi okusun.
 *
 * ---------------------------------------------------------------------------
 * `body` SABİT METİN — ifade dili yok
 * ---------------------------------------------------------------------------
 * Gövdede `{{...}}` yok (bkz. `GraphEdgeValue` başlığı). Motor sabit gövdeye
 * kendi zarfını ekliyor: kural kimliği, koşu kimliği ve hedef özeti. Kullanıcı
 * "hangi kampanya" bilgisini o zarftan okuyor, kendi ifadesini yazarak değil.
 */
const FORBIDDEN_HTTP_HEADERS: ReadonlySet<string> = new Set([
  'authorization',
  'cookie',
  'proxy-authorization',
]);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 IP AYRIŞTIRMA — NOKTALI DÖRTLÜ TEK BİÇİM DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * `isBlockedHttpHost` bir zamanlar yalnız `/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/`
 * biçimini tanıyordu ve o yüzden LOOPBACK'İ SERBEST BIRAKIYORDU. Sunucuda
 * ölçüldü (14 Ağu 2026): `curl http://127.1`, `curl http://2130706433` ve
 * `curl http://0177.0.0.1` üçü de `127.0.0.1`e gidiyor — çünkü `inet_aton`
 * (ve onu kullanan her HTTP istemcisi) 1, 2, 3 ya da 4 parçalı yazımı,
 * sekizliği (`0177`) ve onaltılığı (`0x7f`) kabul ediyor; SON parça kalan tüm
 * baytları yutuyor.
 *
 * Bu yüzden desen eşleme değil AYRIŞTIRMA yapılıyor: adres 32 bitlik sayıya
 * çevriliyor ve aralık ondan soruluyor. Ayrıştırıcı `inet_aton`ın kabul
 * ettiklerini kabul ediyor — daha azını kabul etmek, istemcinin gideceği ama
 * bizim tanımadığımız bir adres bırakmak demek.
 *
 * SAF fonksiyon ve bilerek elle yazılmış: `lib` ES2023 (DOM yok, Node tipi yok)
 * olduğu için `URL` ve `net.isIP` kullanılamıyor — bu dosya tarayıcıda da
 * derleniyor.
 */
function parseIpv4Part(part: string): number | null {
  if (/^0x[0-9a-f]+$/.test(part)) return Number.parseInt(part.slice(2), 16);
  if (/^0[0-7]+$/.test(part)) return Number.parseInt(part.slice(1), 8);
  if (/^\d+$/.test(part)) return Number.parseInt(part, 10);
  return null;
}

/** `inet_aton` semantiği: 1–4 parça, son parça kalan baytları yutar. `null` = IPv4 değil. */
function parseIpv4(host: string): number | null {
  const parts = host.split('.');
  if (parts.length === 0 || parts.length > 4) return null;

  const values: number[] = [];
  for (const part of parts) {
    const value = parseIpv4Part(part);
    if (value === null || !Number.isSafeInteger(value)) return null;
    values.push(value);
  }

  const last = values[values.length - 1];
  if (last === undefined) return null;

  let address = 0;
  for (let i = 0; i < values.length - 1; i += 1) {
    const value = values[i] ?? 0;
    if (value > 0xff) return null; // baş parçalar tek bayt
    address = (address | (value << (8 * (3 - i)))) >>> 0;
  }
  // Son parça kalan (4 - baştakiler) baytı taşıyor: `127.1` → 127.0.0.1.
  if (last >= 2 ** (8 * (5 - values.length))) return null;
  return (address | last) >>> 0;
}

function isBlockedIpv4(address: number): boolean {
  const a = (address >>> 24) & 0xff;
  const b = (address >>> 16) & 0xff;
  if (a === 0 || a === 127) return true;
  if (a === 10) return true;
  /**
   * 🔴 CGNAT — `100.64.0.0/10` (RFC 6598).
   *
   * Eklenmemişti ve ölçüldüğünde `https://100.64.0.1` şema kapısından
   * GEÇİYORDU. "Genel internet" gibi görünen ama görünmeyen bir aralık:
   * operatörler ve BARINDIRMA SAĞLAYICILARI onu iç ağ olarak kullanıyor,
   * yani bu sunucudan erişilebilir bir adres olabilir. RFC 1918 üçlüsünü
   * yazıp bunu atlamak, SSRF süzgecinde en sık yapılan eksik.
   *
   * Sınır `/10`: yalnız `100.64` – `100.127`. `100.128.0.1` GERÇEK bir genel
   * adres ve engellenmemeli.
   */
  if (a === 100 && b >= 64 && b <= 127) return true;
  if (a === 169 && b === 254) return true; // bulut metadata ucu dahil
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  return a >= 224; // multicast + ayrılmış
}

/** IPv6 değişmezini 8 gruba açar. Gömülü IPv4 kuyruğu (`::ffff:127.0.0.1`) dahil. */
function parseIpv6(literal: string): number[] | null {
  const zoneAt = literal.indexOf('%');
  let text = zoneAt >= 0 ? literal.slice(0, zoneAt) : literal;

  // Gömülü IPv4 kuyruğu iki 16-bit gruba çevriliyor; kalan ayrıştırma tek
  // biçimle ilgileniyor. `::ffff:127.0.0.1` → `::ffff:7f00:1`.
  if (text.includes('.')) {
    const cut = text.lastIndexOf(':');
    if (cut < 0) return null;
    const embedded = parseIpv4(text.slice(cut + 1));
    if (embedded === null) return null;
    const high = ((embedded >>> 16) & 0xffff).toString(16);
    const low = (embedded & 0xffff).toString(16);
    text = `${text.slice(0, cut + 1)}${high}:${low}`;
  }

  const halves = text.split('::');
  if (halves.length > 2) return null;

  const groupsOf = (segment: string): number[] | null => {
    if (segment.length === 0) return [];
    const out: number[] = [];
    for (const group of segment.split(':')) {
      if (!/^[0-9a-f]{1,4}$/.test(group)) return null;
      out.push(Number.parseInt(group, 16));
    }
    return out;
  };

  const head = groupsOf(halves[0] ?? '');
  const tail = halves.length === 2 ? groupsOf(halves[1] ?? '') : [];
  if (head === null || tail === null) return null;

  if (halves.length === 2) {
    const fill = 8 - head.length - tail.length;
    if (fill < 1) return null;
    return [...head, ...new Array<number>(fill).fill(0), ...tail];
  }
  return head.length === 8 ? head : null;
}

function isBlockedIpv6(groups: readonly number[]): boolean {
  const first = groups[0] ?? 0;

  // `::` ve `::1` — belirtilmemiş adres ve loopback.
  if (groups.slice(0, 7).every((group) => group === 0)) return true;

  // IPv4-eşlemeli (`::ffff:0:0/96`) ve IPv4-uyumlu (`::/96`) adresler ASIL
  // IPv4'ü taşıyor: karar oradan verilmeli. Node'un `dns.lookup`u family 6'da
  // `::ffff:127.0.0.1` döndürebiliyor — motorun DNS sonrası ikinci kontrolü
  // aynı fonksiyonu çağırdığı için bu dal orada da gerekiyor.
  if (groups.slice(0, 5).every((group) => group === 0)) {
    const marker = groups[5] ?? 0;
    if (marker === 0xffff || marker === 0) {
      return isBlockedIpv4(((((groups[6] ?? 0) << 16) | (groups[7] ?? 0)) >>> 0));
    }
  }

  if ((first & 0xffc0) === 0xfe80) return true; // fe80::/10 link-local
  if ((first & 0xfe00) === 0xfc00) return true; // fc00::/7 unique-local
  return (first & 0xff00) === 0xff00; // ff00::/8 multicast
}

/**
 * Özel/yerel ağa mı işaret ediyor.
 *
 * `hostname` PORTSUZ ve köşeli parantezsiz bir konak adı; ayırma işi
 * `httpsHostname`in.
 */
export function isBlockedHttpHost(hostname: string): boolean {
  // Sondaki kök noktası atılıyor: `localhost.` ve `127.0.0.1.` DNS'te aynı adı
  // gösteriyor ama dizge olarak eşleşmiyordu — ikisi de ölçülerek geçiyordu.
  const host = hostname.trim().toLowerCase().replace(/\.+$/, '');
  if (host.length === 0) return true;
  if (host === 'localhost' || host.endsWith('.localhost')) return true;
  if (host.endsWith('.local') || host.endsWith('.internal') || host.endsWith('.home.arpa')) {
    return true;
  }

  // İki nokta üst üste varsa IPv6 değişmezidir; alan adı olamaz (port zaten
  // ayrıldı). Bu ayrım olmadan `fd`/`fc` ön eki `fdexample.com` gibi meşru
  // alan adlarını da keserdi. Ayrıştırılamayan bir değişmez de KESİLİYOR:
  // meşru bir alan adı olamayacağı için şüphelinin lehine yorum yok.
  if (host.includes(':')) {
    const groups = parseIpv6(host);
    return groups === null ? true : isBlockedIpv6(groups);
  }

  const address = parseIpv4(host);
  return address === null ? false : isBlockedIpv4(address);
}

/** `https://user@host:443/x` → `host`. Ayrıştırılamazsa `null`. */
function httpsHostname(url: string): string | null {
  const match = /^https:\/\/([^/?#]+)/i.exec(url);
  const authority = match?.[1];
  if (authority === undefined || authority.length === 0) return null;

  const at = authority.lastIndexOf('@');
  const hostPort = at >= 0 ? authority.slice(at + 1) : authority;

  if (hostPort.startsWith('[')) {
    const end = hostPort.indexOf(']');
    return end > 1 ? hostPort.slice(1, end).toLowerCase() : null;
  }
  const colon = hostPort.indexOf(':');
  const host = colon >= 0 ? hostPort.slice(0, colon) : hostPort;
  return host.length > 0 ? host.toLowerCase() : null;
}

export const httpActionSchema = z.object({
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).default('POST'),
  url: z
    .url()
    .max(2000)
    .refine((u) => /^https:\/\//i.test(u), { message: 'HTTP_URL_MUST_BE_HTTPS' })
    .refine((u) => {
      const host = httpsHostname(u);
      return host !== null && !isBlockedHttpHost(host);
    }, { message: 'HTTP_URL_BLOCKED_HOST' }),
  headers: z
    .record(
      z.string().regex(/^[A-Za-z0-9-]{1,64}$/, 'Başlık adı geçersiz'),
      z.string().max(500),
    )
    .default({})
    .refine((h) => Object.keys(h).length <= 20, { message: 'HTTP_TOO_MANY_HEADERS' })
    .refine((h) => !Object.keys(h).some((k) => FORBIDDEN_HTTP_HEADERS.has(k.toLowerCase())), {
      message: 'HTTP_CREDENTIAL_HEADER_FORBIDDEN',
    }),
  /// Kayıtlı kimlik bilgisi. Motor başlığı çağrı anında ekliyor; belgeye
  /// hiçbir zaman yazılmıyor.
  credentialId: z.uuid().optional(),
  body: z.string().max(4000).optional(),
  timeoutMs: z.number().int().min(1000).max(30_000).default(10_000),
  /**
   * Çağrı başarısız olursa akış ne yapsın.
   *
   * Varsayılan `fail`: cevap vermeyen bir uca rağmen zincire devam etmek,
   * "haber verdim" sanılan ama verilmemiş bir bildirimin ardından bütçe
   * değiştirmek demek olabilir. Devam etmek İSTENİYORSA seçilir.
   */
  onError: z.enum(['fail', 'continue']).default('fail'),
});
export type HttpActionData = z.infer<typeof httpActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Düğüm
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Düğüm kimliği.
 *
 * Alfabe dar tutuldu (harf, rakam, `_`, `-`) ve bu ileriye dönük bir karar:
 * kimlikler bir gün ifade dilinde düğüme atıf için kullanılacak
 * (`$node["kampanya-filtresi"]`). Nokta, tırnak ve boşluk içeren bir kimlik o
 * günü ayrıştırılamaz hâle getirir ve kayıtlı grafikler geriye dönük
 * düzeltilemez. Kapıyı bugün dar tutmak bedava, yarın genişletmek mümkün.
 */
export const GRAPH_NODE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const graphIdSchema = z.string().regex(GRAPH_NODE_ID_PATTERN, 'Kimlik geçersiz');

export const graphPositionSchema = z.object({
  x: z.number().min(-100_000).max(100_000),
  y: z.number().min(-100_000).max(100_000),
});
export type GraphPosition = z.infer<typeof graphPositionSchema>;

/**
 * Her düğümde ortak alanlar.
 *
 * `position` şemada ve bilerek: tuval yerleşimi grafiğin PARÇASI, ayrı bir
 * tabloda tutulan bir görünüm ayarı değil. Ayrı tutulsaydı bir grafiği dışa
 * aktarmak, şablona çevirmek ya da kopyalamak düzeni kaybettirirdi ve
 * kullanıcı aynı akışı ikinci kez elle dizerdi.
 *
 * `label` isteğe bağlı: kullanıcının düğüme verdiği ad ("hafta sonu kontrolü").
 * Boşsa arayüz cinsin i18n adını gösteriyor. Kimliğin kendisi ad olarak
 * kullanılamaz — kimlik teknik ve değişmez, ad okunur ve değişken.
 */
const graphNodeBase = {
  id: graphIdSchema,
  position: graphPositionSchema,
  label: z.string().trim().max(60).optional(),
};

const scheduleTriggerNode = z.object({
  ...graphNodeBase,
  kind: z.literal('trigger.schedule'),
  data: scheduleTriggerSchema,
});
const metricTriggerNode = z.object({
  ...graphNodeBase,
  kind: z.literal('trigger.metric'),
  data: metricTriggerSchema,
});
const eventTriggerNode = z.object({
  ...graphNodeBase,
  kind: z.literal('trigger.event'),
  data: eventTriggerSchema,
});
const webhookTriggerNode = z.object({
  ...graphNodeBase,
  kind: z.literal('trigger.webhook'),
  data: webhookTriggerSchema,
});
const manualTriggerNode = z.object({
  ...graphNodeBase,
  kind: z.literal('trigger.manual'),
  data: manualTriggerSchema,
});

const scopeNode = z.object({
  ...graphNodeBase,
  kind: z.literal('scope'),
  data: workflowScopeSchema,
});

/**
 * `filter` ve `branch` AYNI şemayı taşıyor — fark davranışta, veride değil.
 * Farklı iki şema yazmak, aynı koşul ağacını iki formda düzenlemek demekti.
 *
 * 🔴 `conditionGroupSchema` DEĞİL `boundedConditionGroupSchema`. Gerekçe o
 * şemanın başlığında ve tek cümlesi şu: v1 kapısı derinlik/kural tavanlarını
 * İKİNCİ bir aşamada (`conditionShapeErrors`) uyguluyor, grafiğin kaydetme
 * kapısında öyle bir aşama yok. Sınırsız şema kullanılsaydı 6 seviyeli bir
 * ağaç buradan geçip `projectionFromGraph` üzerinden v1 `conditions` kolonuna
 * yazılır ve kural v1 editöründe BİR DAHA AÇILAMAZDI — `automation-graph-
 * convert.ts`in ardışık `filter`ları birleştirmeme gerekçesinin ta kendisi.
 */
const filterNode = z.object({
  ...graphNodeBase,
  kind: z.literal('filter'),
  data: boundedConditionGroupSchema,
});
const branchNode = z.object({
  ...graphNodeBase,
  kind: z.literal('branch'),
  data: boundedConditionGroupSchema,
});

const waitNode = z.object({
  ...graphNodeBase,
  kind: z.literal('wait'),
  data: waitNodeSchema,
});

/**
 * 11 aksiyon düğümünün TEK şeması.
 *
 * `data` doğrudan `workflowActionSchema` — 11 ayrı düğüm şeması yazmak, o
 * birleşimi ikinci kez, elle ve eksik yazmak olurdu.
 *
 * 🔴 `kind` ile `data.type` UYUŞMAK ZORUNDA. İkisi de aynı bilgiyi taşıyor ve
 * bu tekrar bilinçli: `kind` grafiğin dili (editör paleti, `GRAPH_NODE_KINDS`,
 * motor sevkiyatı hep onu okuyor), `data.type` aksiyon sözleşmesinin dili
 * (`ACTION_TYPES_WITH_EXECUTOR`, guardrail, log satırı). Tekrarı silmek için
 * `kind`i `'action'`a indirmek grafiği kendi diline yabancılaştırırdı;
 * `data.type`ı silmek `workflowActionSchema`yı kopyalamak demekti.
 *
 * Tekrarın bedeli ikisinin sapabilmesi ve bedel BURADA ödeniyor: sapmış bir
 * düğüm kaydedilemez. `kind: 'action.pause'` + `data.type: 'budget_adjust'`
 * taşıyan bir düğüm, editörde "duraklat" yazan ama bütçe değiştiren bir kutu
 * olurdu.
 */
const actionNode = z
  .object({
    ...graphNodeBase,
    kind: z.enum(GRAPH_ACTION_KINDS),
    data: workflowActionSchema,
  })
  .refine((node) => node.kind === `action.${node.data.type}`, {
    message: 'GRAPH_ACTION_KIND_MISMATCH',
    path: ['kind'],
  });

const httpActionNode = z.object({
  ...graphNodeBase,
  kind: z.literal(GRAPH_HTTP_ACTION_KIND),
  data: httpActionSchema,
});

export const graphNodeSchema = z.discriminatedUnion('kind', [
  scheduleTriggerNode,
  metricTriggerNode,
  eventTriggerNode,
  webhookTriggerNode,
  manualTriggerNode,
  scopeNode,
  filterNode,
  branchNode,
  waitNode,
  actionNode,
  httpActionNode,
]);
export type GraphNode = z.infer<typeof graphNodeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Kenar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 KRİTİK KARAR — KENAR HEDEF KÜMESİ TAŞIR, SERBEST JSON DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * n8n'de kenardan geçen şey serbest JSON öğeleri ve düğümler birbirinin
 * çıktısını bir İFADE DİLİ ile okuyor: `{{$node["X"].json.field}}`. Bu
 * sürümde o dil YOK ve bu bilinçli bir eksiklik, unutulmuş bir özellik değil.
 *
 * ---------------------------------------------------------------------------
 * Neden yok
 * ---------------------------------------------------------------------------
 * 1. ÜÇ AYRI İŞ. İfade dili bir AYRIŞTIRICI, bir GÜVENLİ DEĞERLENDİRİCİ ve
 *    düğüm başına bir ÇIKTI ŞEMASI ister. Değerlendirici sunucuda, kiracının
 *    verisiyle ve kullanıcının yazdığı metinle çalışıyor — bu bir enjeksiyon
 *    yüzeyi ve kendi tasarımını, kendi testini hak ediyor. Sözleşmeye
 *    sıkıştırılamaz.
 *
 * 2. BUGÜN KİMSE KULLANMAZDI. 11 aksiyonun 11'i de parametresini KENDİ
 *    `data`sından alıyor; hiçbiri yukarıdaki düğümden bir alan okumuyor.
 *    Grafikteki tüm düğümler zaten TEK bir dil konuşuyor: `scope` hedef kümesi
 *    üretiyor, `filter` daraltıyor, `branch` ikiye bölüyor, aksiyon uyguluyor.
 *    Serbest JSON bugün hiçbir şey satın almaz, yalnız değerlendiriciyi
 *    borçlandırırdı.
 *
 * 3. GRAFİK BELGESİ KÜÇÜK KALIR. `GraphEdge`de `data` alanı YOK. Olsaydı
 *    editör oraya eşleme kuralları yazmaya başlardı ve motor onları
 *    uygulayamadığı için sessizce yok sayardı — Faz 0'ın üç canlı hatasının
 *    tam olarak sınıfı: sözleşmesi olan ama uygulayıcısı olmayan alan.
 *
 * ---------------------------------------------------------------------------
 * İleriye nasıl açık bırakıldı
 * ---------------------------------------------------------------------------
 * Kenarın taşıdığı şey çıplak bir dizi (`ScopeTarget[]`) DEĞİL, bir DEĞER
 * NESNESİ (`GraphEdgeValue`). İfade dili geldiğinde `targets`ın yanına ikinci
 * bir alan eklenir ve grafik BELGESİ hiç değişmez — çünkü belge yükü hiçbir
 * zaman tarif etmedi. `version` 2'de kalır.
 *
 * ---------------------------------------------------------------------------
 * `TTarget` neden jenerik
 * ---------------------------------------------------------------------------
 * Hedefin gerçek tipi `ScopeTarget` ve o tip `apps/api/src/automation/
 * automation.engine.ts` içinde (ikizi worker'da). `packages/shared` apps'i
 * import EDEMEZ — bağımlılık yönü tek taraflı. Buraya bir kopyasını yazmak iki
 * tipin sessizce sapması demekti (`platform` ve `cpcBid` alanları Faz 5'te
 * eklendi; kopya o gün eskirdi). Jenerik bırakmak, motorun kendi tipini
 * takmasını sağlıyor: `GraphEdgeValue<ScopeTarget>`.
 */
export interface GraphEdgeValue<TTarget> {
  /// Bu kenardan aşağı akan hedefler. BOŞ OLABİLİR: `filter` hiçbir hedefi
  /// geçirmediğinde kenar akmaya devam eder ama küme boştur ve aşağıdaki
  /// aksiyon hiçbir şey uygulamaz. "Kenar akmadı" ile "boş küme aktı" farklı
  /// iki gerçek; ikincisi koşu kaydında görünmeli.
  readonly targets: readonly TTarget[];
}

/**
 * Bir düğümün ürettiği port → değer eşlemesi. Motorun düğüm çalıştırıcısı
 * bunu döndürüyor.
 *
 * `Partial` çünkü `branch` iki portu da doldururken diğer cinsler yalnız
 * `out`u dolduruyor; ayrıca `action.http` hata alıp (`onError: 'fail'`) hiçbir
 * portu doldurmayabilir — o durumda zincir orada biter.
 */
export type GraphNodeOutput<TTarget> = Partial<Record<GraphPort, GraphEdgeValue<TTarget>>>;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 AKSİYON DÜĞÜMÜ HEDEF KÜMESİNİ DEĞİŞTİRMEDEN GEÇİRİR
 * ═══════════════════════════════════════════════════════════════════════════
 * `ACTION_COUNTER_COUNTS_ONLY` ile aynı cinsten bir sözleşme kararı: doküman
 * söylemiyor, motor bir cevap vermek zorunda ve iki makul cevaptan yalnız biri
 * doğru.
 *
 * Kural şu: `action.*` düğümü `out` portuna GİRDİĞİ kümenin AYNISINI koyar.
 * Aksiyonun o hedefte `APPLIED`, `SKIPPED`, `AWAITING_APPROVAL` ya da `FAILED`
 * olması kümeyi DARALTMAZ.
 *
 * ---------------------------------------------------------------------------
 * Neden — v1 davranışını korumak zorunda
 * ---------------------------------------------------------------------------
 * Doğrusal kuralda aksiyonlar BİRBİRİNDEN BAĞIMSIZ: `planRun` her aksiyonu
 * `matched` kümesinin TAMAMIYLA planlıyor (`automation.engine.ts` →
 * `workflow.actions.forEach`). Bir aksiyonun guardrail'e takılması sonrakini
 * etkilemiyor.
 *
 * `graphFromLegacy` o kuralı bir ZİNCİRE açıyor (zincir aksiyon SIRASINI
 * taşımak için gerekli — "önce duraklat sonra bildir" ile tersi farklı
 * kurallar). Zincirde daraltma yapan bir motor, dönüştürülen her v1 kuralının
 * davranışını SESSİZCE değiştirirdi.
 *
 * Somut kayıp senaryosu, ve tam da en çok kurulan kural biçiminde:
 *
 *     bütçeyi %20 artır  →  bana bildir
 *
 * Artış çalışma alanı tavanına takılıp `SKIPPED` olduğunda, daraltan bir
 * motorda bildirim HİÇ GİTMEZ. Yani kullanıcı, kuralının çalışmadığını tam
 * olarak öğrenmesi gereken anda öğrenemez. Bu, bu deponun `notify`yi neden
 * ayrı bir aksiyon yaptığının da gerekçesini çiğnerdi.
 *
 * ---------------------------------------------------------------------------
 * Daraltmak İSTENİYORSA doğru araç `filter`
 * ---------------------------------------------------------------------------
 * "Yalnız bütçesi gerçekten artanlara bildir" meşru bir istek ama onun yeri
 * aksiyonun yan etkisi değil, araya konan açık bir düğüm. Bugün böyle bir
 * `filter` yok (koşullar metriğe bakıyor, önceki aksiyonun sonucuna değil) ve
 * bu bilinçli bir eksik: aksiyon sonucuna bakan bir süzgeç, kenarın serbest
 * JSON taşımasını gerektirir — yani ifade dilini (bkz. `GraphEdgeValue`).
 *
 * ⚠️ `action.http` İSTİSNA ve tek istisna: `onError: 'fail'` iken hata alan
 * düğüm hiçbir portu doldurmaz ve zincir orada biter. Gerekçesi ters yönde ve
 * aynı derecede somut — cevap vermeyen bir uca rağmen devam etmek, "haber
 * verdim" sanılan bir bildirimin ardından bütçe değiştirmek demek olabilir.
 * Fark, birinin BİZİM guardrail'imize takılması, diğerinin DIŞARIDAN cevap
 * alamaması: ilkinde ne olduğunu biliyoruz, ikincisinde bilmiyoruz.
 */
export const ACTION_NODE_TARGET_SEMANTICS = 'pass_through' as const;

/**
 * Kenar.
 *
 * `fromPort` varsayılanı `out`: `branch` dışındaki her düğüm için tek doğru
 * değer o ve editörün her kenara elle yazması gereksiz tekrar olurdu.
 * `branch`ten çıkan bir kenarda varsayılanın kalması ise HATA ve şekil
 * denetleyicisi bunu `GRAPH_BRANCH_INVALID_PORT` ile söylüyor — sessizce
 * `true` dalına saymak, kullanıcının çizmediği bir akışı uygulamak olurdu.
 */
export const graphEdgeSchema = z.object({
  id: graphIdSchema,
  from: graphIdSchema,
  fromPort: graphPortSchema.default('out'),
  to: graphIdSchema,
});
export type GraphEdge = z.infer<typeof graphEdgeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Grafik belgesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Yerel şekil — düğümlerin ve kenarların TEK TEK geçerliliği, dizi tavanları.
 *
 * Grafiğin BÜTÜNÜNE dair hiçbir şey söylemiyor (döngü, erişilebilirlik, kaç
 * kapsam düğümü var…). O denetimler `graphShapeErrors`ta ve ayrımın gerekçesi
 * `conditionGroupSchema` ile `conditionShapeErrors` arasındaki ayrımın
 * aynısı: zod bir seviyeyi görebiliyor, toplamı göremiyor.
 *
 * Bu şema, editörün YARIM bir taslağı ayrıştırmak için kullanacağı gevşek
 * kapı. Kaydetmede `workflowGraphSchema` kullanılır.
 */
export const graphDocumentSchema = z.object({
  version: z.literal(GRAPH_SCHEMA_VERSION).default(GRAPH_SCHEMA_VERSION),
  nodes: z.array(graphNodeSchema).min(1).max(MAX_GRAPH_NODES),
  edges: z.array(graphEdgeSchema).max(MAX_GRAPH_EDGES).default([]),
});

/**
 * 🔴 KAYDETME KAPISI — yerel şekil + bütün grafiğe dair denetimler.
 *
 * `graphShapeErrors`ı superRefine içinden ÇAĞIRIYOR, kendi kopyasını yazmıyor.
 * İki ayrı uygulama olsaydı editörün gösterdiği hata listesi ile sunucunun
 * reddetme sebebi ayrışabilirdi — kullanıcı ekranda yeşil gördüğü bir grafiği
 * kaydedemezdi ve neden olduğunu okuyamazdı.
 *
 * Hata mesajı olarak KOD basılıyor (`GRAPH_CYCLE`), düz metin değil: arayüz
 * çeviriyor ve `params` ile birlikte "hangi düğüm" bilgisini de veriyor.
 * `ValidationIssue.messageKey` ile aynı desen.
 */
export const workflowGraphSchema = graphDocumentSchema.superRefine((graph, ctx) => {
  for (const issue of graphShapeErrors(graph)) {
    const nodeId = issue.params.nodeId;
    const edgeId = issue.params.edgeId;
    const nodeIndex =
      typeof nodeId === 'string' ? graph.nodes.findIndex((n) => n.id === nodeId) : -1;
    const edgeIndex =
      typeof edgeId === 'string' ? graph.edges.findIndex((e) => e.id === edgeId) : -1;

    ctx.addIssue({
      code: 'custom',
      message: issue.code,
      params: issue.params,
      path:
        nodeIndex >= 0
          ? ['nodes', nodeIndex]
          : edgeIndex >= 0
            ? ['edges', edgeIndex]
            : [],
    });
  }
});
export type WorkflowGraph = z.infer<typeof workflowGraphSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Şekil doğrulaması
// ═══════════════════════════════════════════════════════════════════════════

export const GraphErrorCode = {
  /// Hiç düğüm yok.
  GRAPH_EMPTY: 'GRAPH_EMPTY',
  GRAPH_TOO_MANY_NODES: 'GRAPH_TOO_MANY_NODES',
  GRAPH_TOO_MANY_EDGES: 'GRAPH_TOO_MANY_EDGES',
  GRAPH_DUPLICATE_NODE_ID: 'GRAPH_DUPLICATE_NODE_ID',
  GRAPH_DUPLICATE_EDGE_ID: 'GRAPH_DUPLICATE_EDGE_ID',
  /// Kenarın bir ucu var olmayan bir düğüme bakıyor.
  GRAPH_EDGE_UNKNOWN_NODE: 'GRAPH_EDGE_UNKNOWN_NODE',
  GRAPH_EDGE_SELF_LOOP: 'GRAPH_EDGE_SELF_LOOP',
  /// Kapsam düğümü yok — hedef üretecek hiçbir şey yok.
  GRAPH_SCOPE_MISSING: 'GRAPH_SCOPE_MISSING',
  /// Birden fazla kapsam düğümü. Bkz. `graphShapeErrors` başlığı.
  GRAPH_SCOPE_NOT_UNIQUE: 'GRAPH_SCOPE_NOT_UNIQUE',
  GRAPH_TRIGGER_MISSING: 'GRAPH_TRIGGER_MISSING',
  /// Tetikleyiciye kenar giriyor — tetikleyici bir KAYNAK, girdisi olamaz.
  GRAPH_TRIGGER_HAS_INPUT: 'GRAPH_TRIGGER_HAS_INPUT',
  /// Tetikleyiciden çıkan kenar kapsam düğümünden başka bir yere gidiyor.
  GRAPH_TRIGGER_MUST_FEED_SCOPE: 'GRAPH_TRIGGER_MUST_FEED_SCOPE',
  /// Tetikleyicinin kapsama giden kenarı yok — hiçbir zaman hiçbir şey yapmaz.
  GRAPH_TRIGGER_NOT_CONNECTED: 'GRAPH_TRIGGER_NOT_CONNECTED',
  /// Kapsam düğümüne tetikleyici olmayan bir yerden kenar giriyor.
  GRAPH_SCOPE_INPUT_NOT_TRIGGER: 'GRAPH_SCOPE_INPUT_NOT_TRIGGER',
  /// Kapsam dışında bir düğüme birden fazla kenar giriyor (birleştirme yok).
  GRAPH_NODE_MULTIPLE_INPUTS: 'GRAPH_NODE_MULTIPLE_INPUTS',
  /// Hedef tüketen bir düğüm kapsamın yukarısında.
  GRAPH_NODE_BEFORE_SCOPE: 'GRAPH_NODE_BEFORE_SCOPE',
  /// Hiçbir tetikleyiciden ulaşılamıyor — çizilmiş ama ölü.
  GRAPH_UNREACHABLE_NODE: 'GRAPH_UNREACHABLE_NODE',
  GRAPH_CYCLE: 'GRAPH_CYCLE',
  GRAPH_TOO_DEEP: 'GRAPH_TOO_DEEP',
  GRAPH_TOO_MANY_ACTIONS: 'GRAPH_TOO_MANY_ACTIONS',
  /// Bir YOL üzerinde birden fazla `wait` düğümü. Bkz. `graphShapeErrors`.
  GRAPH_WAIT_MULTIPLE_ON_PATH: 'GRAPH_WAIT_MULTIPLE_ON_PATH',
  /// `branch`in `true` ya da `false` portundan hiç kenar çıkmıyor.
  GRAPH_BRANCH_PORT_EMPTY: 'GRAPH_BRANCH_PORT_EMPTY',
  /// `branch`ten çıkan kenar `true`/`false` dışında bir port kullanıyor.
  GRAPH_BRANCH_INVALID_PORT: 'GRAPH_BRANCH_INVALID_PORT',
  /// `branch` olmayan bir düğümden `out` dışında bir portla kenar çıkıyor.
  GRAPH_PORT_UNKNOWN: 'GRAPH_PORT_UNKNOWN',
  /**
   * Düğüm cinsi ile `data.type` uyuşmuyor. Zod refine'ı basıyor; buradaki
   * kayıt vokabülerin TEK yerde toplanması için — arayüz iki listeye
   * bakmasın.
   */
  GRAPH_ACTION_KIND_MISMATCH: 'GRAPH_ACTION_KIND_MISMATCH',
} as const;
export type GraphErrorCode = (typeof GraphErrorCode)[keyof typeof GraphErrorCode];

export interface GraphShapeError {
  code: GraphErrorCode;
  /// `ValidationIssue.params` ile aynı biçim — arayüz i18n metnine gömüyor.
  params: Record<string, string | number>;
}

/**
 * `graphShapeErrors` girdisi.
 *
 * `WorkflowGraph` DEĞİL ve bu bilinçli: editör tuvalinde duran taslak çoğu
 * zaman yarım (aksiyonun bütçe yüzdesi henüz girilmemiş, kapsam boş) ama
 * ŞEKLİ zaten denetlenebilir — kullanıcı kutuları bağladıkça döngüyü ve
 * kopuk düğümü anında görmeli, `data` alanlarını doldurmayı beklemeden.
 * Yapısal olarak `WorkflowGraph` bu tipe atanabiliyor, o yüzden kaydetme
 * yolunda ikinci bir dönüşüm gerekmiyor.
 */
export interface GraphShapeNode {
  readonly id: string;
  readonly kind: string;
}
export interface GraphShapeEdge {
  readonly id: string;
  readonly from: string;
  readonly fromPort?: string;
  readonly to: string;
}
export interface GraphShape {
  readonly nodes: readonly GraphShapeNode[];
  readonly edges: readonly GraphShapeEdge[];
}

/** Kenarları `from` → `to` bitişiklik listesine çevirir. */
function adjacency(edges: readonly GraphShapeEdge[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const edge of edges) {
    const list = out.get(edge.from);
    if (list) list.push(edge.to);
    else out.set(edge.from, [edge.to]);
  }
  return out;
}

/** `from` kümesinden ileri doğru ulaşılabilen düğüm kimlikleri (başlangıçlar dahil). */
function reachableFrom(starts: readonly string[], next: Map<string, string[]>): Set<string> {
  const seen = new Set<string>(starts);
  const queue = [...starts];
  while (queue.length > 0) {
    const current = queue.pop();
    if (current === undefined) break;
    for (const target of next.get(current) ?? []) {
      if (seen.has(target)) continue;
      seen.add(target);
      queue.push(target);
    }
  }
  return seen;
}

/**
 * Kahn'ın KISMİ çıktısı: yerleştirilebilen düğümler, sırayla.
 *
 * Döngüde tam sıra üretilemiyor ama yerleştirilemeyen düğümlerin KİMLER
 * olduğu döngünün kendisi demek — `graphShapeErrors` kullanıcıya "şu kutudan
 * takip et" diyebilsin diye kısmi çıktı burada tutuluyor. `null` döndürüp
 * bilgiyi atmak, çağıranı ikinci bir tarama yazmaya zorlardı.
 */
function kahnOrder(nodes: readonly GraphShapeNode[], edges: readonly GraphShapeEdge[]): string[] {
  const next = adjacency(edges);

  const indegree = new Map<string, number>();
  for (const node of nodes) indegree.set(node.id, 0);
  for (const edge of edges) indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1);

  const queue = [...indegree.keys()].filter((id) => indegree.get(id) === 0);
  const order: string[] = [];

  while (queue.length > 0) {
    const current = queue.shift();
    if (current === undefined) break;
    order.push(current);
    for (const target of next.get(current) ?? []) {
      const left = (indegree.get(target) ?? 0) - 1;
      indegree.set(target, left);
      if (left === 0) queue.push(target);
    }
  }
  return order;
}

/** Ucu bilinmeyen kenarları atar — traversal olmayan bir kimliği takip etmesin. */
function usableEdges(graph: GraphShape): GraphShapeEdge[] {
  const ids = new Set(graph.nodes.map((n) => n.id));
  return graph.edges.filter((e) => ids.has(e.from) && ids.has(e.to) && e.from !== e.to);
}

/**
 * Yürütme sırası — Kahn. Döngü varsa `null`.
 *
 * Motorun ihtiyacı olan şey bu: düğümleri hangi sırayla çalıştıracağı. Sırayı
 * motorun kendi içinde ikinci kez çıkarması, kuru çalıştırma ile gerçek
 * koşunun farklı sıralar üretebilmesi demekti — `condition-eval.ts`'in ikiz
 * dosya olmasıyla aynı gerekçe.
 *
 * Aynı kimlikli iki düğüm SIRAYA bir kez girer (şekil denetleyicisi tekrarı
 * ayrıca hata sayıyor); burada `null` dönmüyor çünkü bu fonksiyonun cevabı
 * yalnız DÖNGÜ sorusuna ait.
 */
export function graphTopologicalOrder(graph: GraphShape): string[] | null {
  const byId = new Map(graph.nodes.map((n) => [n.id, n] as const));
  const order = kahnOrder([...byId.values()], usableEdges(graph));
  return order.length === byId.size ? order : null;
}

/**
 * En uzun yolun DÜĞÜM sayısı. Döngü varsa `null` (tanımsız).
 *
 * `conditionDepth`in grafikteki karşılığı: kök 1 sayılır, her adım bir
 * seviye ekler.
 */
export function graphDepth(graph: GraphShape): number | null {
  const order = graphTopologicalOrder(graph);
  if (order === null) return null;

  const next = adjacency(usableEdges(graph));

  // Ters topolojik sırada yürüyünce her düğümün ardılları zaten hesaplanmış
  // olur — özyineleme ve yığın derinliği gerekmez.
  const longest = new Map<string, number>();
  let deepest = 0;
  for (let i = order.length - 1; i >= 0; i -= 1) {
    const id = order[i];
    if (id === undefined) continue;
    let best = 1;
    for (const target of next.get(id) ?? []) {
      best = Math.max(best, 1 + (longest.get(target) ?? 1));
    }
    longest.set(id, best);
    deepest = Math.max(deepest, best);
  }
  return deepest;
}

/**
 * 🔴 ZOD'UN İFADE EDEMEDİKLERİ — grafiğin BÜTÜNÜNE dair denetimler.
 *
 * `conditionShapeErrors` ile aynı imza, aynı ruh: saf fonksiyon, `{code,
 * params}` listesi döndürür, boş liste "şekil sağlam" demek. Hem editör (her
 * tuş vuruşunda) hem kaydetme (son kapı olarak `workflowGraphSchema` üzerinden)
 * AYNI fonksiyonu çağırıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Uygulanan kurallar ve gerekçeleri
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * TEK KAPSAM DÜĞÜMÜ. Kapsam, akışın konuştuğu tek dili (hedef kümesi)
 * üretiyor. İki kapsam düğümü, aşağıda birleşen iki farklı evren demekti ve
 * "aynı kampanya iki koldan geldi, aksiyon iki kez mi uygulanacak" sorusunun
 * cevabı yok. Bir tane, herkes ondan besleniyor.
 *
 * FAN-IN YASAK — KAPSAM HARİÇ. Kapsam dışında hiçbir düğüme birden fazla kenar
 * giremez. Birleştirme (`merge`) bir SEMANTİK seçim gerektiriyor: birleşen iki
 * kümenin birleşimi mi alınacak, kesişimi mi; ve `wait` düğümleri varken
 * "hepsini bekle" bir zamanlama problemine dönüşüyor. Üç cevap da savunulabilir
 * olduğu için hiçbiri SESSİZCE seçilemez. `merge` düğümü v3'ün işi.
 *
 *   ⚠️ Bunun bilinen bedeli: `branch`in iki dalı aynı aksiyonda BİRLEŞEMİYOR,
 *   aksiyon iki kez çizilmek zorunda ve ikisi de `MAX_ACTIONS_PER_GRAPH`
 *   tavanından yiyor. Bedel kabul edildi çünkü alternatifi tanımsız bir
 *   birleştirme kuralıyla para hareket ettirmekti.
 *
 * KAPSAM İSTİSNASI. Kapsam düğümüne birden fazla kenar girebilir ama YALNIZ
 * tetikleyicilerden. Bu bir birleştirme değil bir ALTERNATİF: bir koşuda tek
 * bir tetikleyici ateşliyor. "Çoklu tetikleyici" (aynı akış hem sabah 9'da hem
 * CPA fırlayınca çalışsın) tam olarak bu.
 *
 * TETİKLEYİCİ YALNIZ KAPSAMA BAĞLANIR. Tetikleyiciden doğrudan bir `filter`a
 * giden kenar, hedefi olmayan bir süzgeç demek. Kural tek cümleyle
 * anlatılabilir olduğu için ayrı bir kod hak ediyor.
 *
 * DÖNGÜ YASAK (DAG). Döngü bir akışı sonsuz kılar ve bu akış PARA HAREKET
 * ETTİRİYOR. Yineleme sayacı, bütçesi ve durdurma koşulu olan bir döngü ayrı
 * bir tasarım; sözleşme onu ifade etmiyor.
 *
 * ERİŞİLEMEYEN DÜĞÜM. Tuvalde duran ama hiçbir tetikleyiciden ulaşılamayan bir
 * kutu, kullanıcının yaptığını sandığı ama yapmayan bir şey. Hata, uyarı değil:
 * bu deponun en pahalı hata sınıfı sessizce çalışmayan koddur.
 *
 * ---------------------------------------------------------------------------
 * Sıra önemli
 * ---------------------------------------------------------------------------
 * Yapısal sorunlar (kimlik, kenar ucu) önce; onlar varken bağlantı denetimleri
 * yanlış sonuç üretir ve kullanıcıya asıl sorunu değil türevini gösterirdi.
 */
export function graphShapeErrors(graph: GraphShape): GraphShapeError[] {
  const errors: GraphShapeError[] = [];
  const fail = (code: GraphErrorCode, params: Record<string, string | number> = {}): void => {
    errors.push({ code, params });
  };

  const { nodes, edges } = graph;

  if (nodes.length === 0) {
    fail(GraphErrorCode.GRAPH_EMPTY);
    return errors; // Kenar denetimlerinin bundan sonrası anlamsız.
  }
  if (nodes.length > MAX_GRAPH_NODES) {
    fail(GraphErrorCode.GRAPH_TOO_MANY_NODES, { count: nodes.length, max: MAX_GRAPH_NODES });
  }
  if (edges.length > MAX_GRAPH_EDGES) {
    fail(GraphErrorCode.GRAPH_TOO_MANY_EDGES, { count: edges.length, max: MAX_GRAPH_EDGES });
  }

  // ── Kimlikler ────────────────────────────────────────────────────────────
  const byId = new Map<string, GraphShapeNode>();
  for (const node of nodes) {
    if (byId.has(node.id)) fail(GraphErrorCode.GRAPH_DUPLICATE_NODE_ID, { nodeId: node.id });
    else byId.set(node.id, node);
  }

  const seenEdgeIds = new Set<string>();
  for (const edge of edges) {
    if (seenEdgeIds.has(edge.id)) fail(GraphErrorCode.GRAPH_DUPLICATE_EDGE_ID, { edgeId: edge.id });
    else seenEdgeIds.add(edge.id);
  }

  // ── Kenar uçları ─────────────────────────────────────────────────────────
  // Ucu bilinmeyen kenar sonraki denetimlerden ÇIKARILIYOR: haritada olmayan
  // bir kimliği takip etmek, olmayan bir kopukluğu rapor etmek olurdu.
  const usable: GraphShapeEdge[] = [];
  for (const edge of edges) {
    const fromKnown = byId.has(edge.from);
    const toKnown = byId.has(edge.to);
    if (!fromKnown) fail(GraphErrorCode.GRAPH_EDGE_UNKNOWN_NODE, { edgeId: edge.id, nodeId: edge.from });
    if (!toKnown) fail(GraphErrorCode.GRAPH_EDGE_UNKNOWN_NODE, { edgeId: edge.id, nodeId: edge.to });
    if (!fromKnown || !toKnown) continue;
    if (edge.from === edge.to) {
      fail(GraphErrorCode.GRAPH_EDGE_SELF_LOOP, { edgeId: edge.id, nodeId: edge.from });
      continue;
    }
    usable.push(edge);
  }

  // ── Sayımlar ─────────────────────────────────────────────────────────────
  const scopeNodes = nodes.filter((n) => n.kind === 'scope');
  const scopeId = scopeNodes.length === 1 ? scopeNodes[0]?.id ?? null : null;
  if (scopeNodes.length === 0) fail(GraphErrorCode.GRAPH_SCOPE_MISSING);
  else if (scopeNodes.length > 1) {
    fail(GraphErrorCode.GRAPH_SCOPE_NOT_UNIQUE, { count: scopeNodes.length });
  }

  const triggerNodes = nodes.filter((n) => isGraphTriggerKind(n.kind));
  if (triggerNodes.length === 0) fail(GraphErrorCode.GRAPH_TRIGGER_MISSING);

  const actionCount = nodes.filter((n) => isGraphActionKind(n.kind)).length;
  if (actionCount > MAX_ACTIONS_PER_GRAPH) {
    fail(GraphErrorCode.GRAPH_TOO_MANY_ACTIONS, {
      count: actionCount,
      max: MAX_ACTIONS_PER_GRAPH,
    });
  }

  // ── Portlar ──────────────────────────────────────────────────────────────
  const outgoing = new Map<string, GraphShapeEdge[]>();
  const incoming = new Map<string, GraphShapeEdge[]>();
  const bucket = (map: Map<string, GraphShapeEdge[]>, key: string): GraphShapeEdge[] => {
    const existing = map.get(key);
    if (existing) return existing;
    const created: GraphShapeEdge[] = [];
    map.set(key, created);
    return created;
  };
  for (const edge of usable) {
    bucket(outgoing, edge.from).push(edge);
    bucket(incoming, edge.to).push(edge);
  }

  for (const edge of usable) {
    const port = edge.fromPort ?? 'out';
    const source = byId.get(edge.from);
    if (source?.kind === 'branch') {
      if (port !== 'true' && port !== 'false') {
        fail(GraphErrorCode.GRAPH_BRANCH_INVALID_PORT, { edgeId: edge.id, port });
      }
    } else if (port !== 'out') {
      fail(GraphErrorCode.GRAPH_PORT_UNKNOWN, { edgeId: edge.id, port });
    }
  }

  for (const node of nodes) {
    if (node.kind !== 'branch') continue;
    const ports = new Set((outgoing.get(node.id) ?? []).map((e) => e.fromPort ?? 'out'));
    if (!ports.has('true')) fail(GraphErrorCode.GRAPH_BRANCH_PORT_EMPTY, { nodeId: node.id, port: 'true' });
    if (!ports.has('false')) fail(GraphErrorCode.GRAPH_BRANCH_PORT_EMPTY, { nodeId: node.id, port: 'false' });
  }

  // ── Giriş dereceleri ─────────────────────────────────────────────────────
  for (const node of nodes) {
    const inputs = incoming.get(node.id) ?? [];

    if (isGraphTriggerKind(node.kind)) {
      if (inputs.length > 0) fail(GraphErrorCode.GRAPH_TRIGGER_HAS_INPUT, { nodeId: node.id });
      continue;
    }

    if (node.id === scopeId) {
      for (const edge of inputs) {
        const source = byId.get(edge.from);
        if (source && !isGraphTriggerKind(source.kind)) {
          fail(GraphErrorCode.GRAPH_SCOPE_INPUT_NOT_TRIGGER, { edgeId: edge.id, nodeId: edge.from });
        }
      }
      continue;
    }

    if (inputs.length > 1) {
      fail(GraphErrorCode.GRAPH_NODE_MULTIPLE_INPUTS, { nodeId: node.id, count: inputs.length });
    }
  }

  // ── Tetikleyici bağlantıları ─────────────────────────────────────────────
  if (scopeId !== null) {
    for (const trigger of triggerNodes) {
      const out = outgoing.get(trigger.id) ?? [];
      for (const edge of out) {
        if (edge.to !== scopeId) {
          fail(GraphErrorCode.GRAPH_TRIGGER_MUST_FEED_SCOPE, { edgeId: edge.id, nodeId: trigger.id });
        }
      }
      if (!out.some((e) => e.to === scopeId)) {
        fail(GraphErrorCode.GRAPH_TRIGGER_NOT_CONNECTED, { nodeId: trigger.id });
      }
    }
  }

  // ── Döngü, erişilebilirlik, derinlik ─────────────────────────────────────
  const distinctNodes = [...byId.values()];
  const placed = kahnOrder(distinctNodes, usable);

  if (placed.length !== byId.size) {
    // Topolojik sıraya giremeyen düğümler döngünün üstünde. Hepsini birden
    // basmak yerine TEK bir kimlik veriliyor: kullanıcı zinciri o kutudan
    // takip edebilir ve ekran yirmi kırmızı kutuya dönmez.
    const placedIds = new Set(placed);
    const stuck = distinctNodes.find((n) => !placedIds.has(n.id));
    fail(GraphErrorCode.GRAPH_CYCLE, stuck ? { nodeId: stuck.id } : {});
  } else {
    // Derinlik yalnız DAG'de tanımlı; döngü varken hesaplamak sonsuz yol
    // ölçmek olurdu. `graphDepth` bu yüzden ikinci kez sıralıyor — grafik
    // 40 düğümle sınırlı, maliyet ölçülemez.
    const depth = graphDepth({ nodes: distinctNodes, edges: usable });
    if (depth !== null && depth > MAX_GRAPH_DEPTH) {
      fail(GraphErrorCode.GRAPH_TOO_DEEP, { depth, max: MAX_GRAPH_DEPTH });
    }

    /**
     * 🔴 BİR YOLDA TEK `wait` (Faz 3).
     *
     * ═══════════════════════════════════════════════════════════════════════
     * Neden GRAFİKTE değil YOLDA sayılıyor
     * ═══════════════════════════════════════════════════════════════════════
     * "Grafikte tek bekleme" demek, `branch`in iki dalına ayrı ayrı bekleme
     * koymayı da yasaklardı — oysa o iki dal AYNI koşuda birlikte akmıyor
     * (biri geçenleri, diğeri geçmeyenleri taşıyor) ve her birinin kendi
     * beklemesi meşru: "iyi gidenleri 1 gün sonra artır, kötüleri 3 gün sonra
     * duraklat". Yasak, aynı hedefin ARDIŞIK iki kez uyutulmasına.
     *
     * Ardışık bekleme neden yasak:
     *   • KOŞU ÖMRÜ BAĞLANAMAZ. Tek bekleme 7 günle sınırlı; iki ardışık
     *     bekleme 14, üçü 21 gün eder ve `MAX_RUN_LIFETIME_MINUTES` bir
     *     tavan olmaktan çıkar.
     *   • DEVAM DURUMU ÇOĞALIR. `WorkflowRun.resumeAt`/`resumeState` bir
     *     koşunun askıya alınmış dallarını taşıyor; ardışık bekleme, uyanan
     *     bir koşunun kendini İKİNCİ kez askıya alması demek — koşu haftalarca
     *     `RUNNING` kalır ve "bitti mi" sorusunun cevabı okunamaz hâle gelir.
     *   • KARARIN DAYANAĞI ESKİR. İkinci beklemenin sonunda uygulanan aksiyon,
     *     iki hafta önceki metriğe bakılarak kurulmuş bir kararın devamı olur.
     *
     * `error`, uyarı değil: ikinci bekleme motorda sessizce yok sayılsaydı
     * kullanıcı çizdiği akıştan farklı bir şey çalıştırırdı; motorda
     * desteklenseydi yukarıdaki üç bedelin hepsi ödenirdi.
     *
     * Sayım topolojik sırada yürüyor — `placed` tam bir sıra olduğu için her
     * düğümün SELEFLERİ ondan önce hesaplanmış oluyor.
     */
    const waitsBefore = new Map<string, number>();
    for (const id of placed) {
      let inherited = 0;
      for (const edge of incoming.get(id) ?? []) {
        inherited = Math.max(inherited, waitsBefore.get(edge.from) ?? 0);
      }
      const isWait = byId.get(id)?.kind === 'wait';
      const total = inherited + (isWait ? 1 : 0);
      waitsBefore.set(id, total);
      // Hata İKİNCİ beklemede basılıyor, birincide değil: kullanıcının
      // sileceği ya da taşıyacağı kutu o.
      if (isWait && total > 1) {
        fail(GraphErrorCode.GRAPH_WAIT_MULTIPLE_ON_PATH, { nodeId: id, count: total });
      }
    }
  }

  const next = adjacency(usable);

  const unreachable = new Set<string>();
  if (triggerNodes.length > 0) {
    // Tetikleyici yoksa denetim atlanıyor: `GRAPH_TRIGGER_MISSING` zaten
    // söylendi ve her düğümü "erişilemez" diye işaretlemek gürültü olurdu.
    const reachable = reachableFrom(triggerNodes.map((n) => n.id), next);
    for (const node of nodes) {
      if (reachable.has(node.id)) continue;
      unreachable.add(node.id);
      fail(GraphErrorCode.GRAPH_UNREACHABLE_NODE, { nodeId: node.id });
    }
  }

  if (scopeId !== null) {
    const afterScope = reachableFrom([scopeId], next);
    for (const node of nodes) {
      if (!CONSUMER_KIND_SET.has(node.kind)) continue;
      if (afterScope.has(node.id)) continue;
      // Hiçbir tetikleyiciden ulaşılamayan bir kutu zaten kopuk; ona bir de
      // "kapsamın yukarısında" demek AYNI sorunu ikinci kez göstermek olurdu.
      // Kullanıcının yapması gereken tek şey var: kutuyu bağlamak.
      if (unreachable.has(node.id)) continue;
      fail(GraphErrorCode.GRAPH_NODE_BEFORE_SCOPE, { nodeId: node.id });
    }
  }

  return errors;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sözleşme vs gerçek
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 MOTORUN BUGÜN GERÇEKTEN ÇALIŞTIRDIĞI DÜĞÜM CİNSLERİ.
 *
 * `ACTION_TYPES_WITH_EXECUTOR` ve `WORKFLOW_EVENTS_WITH_PUBLISHER` ile aynı
 * fikir, üçüncü eksen: `GRAPH_NODE_KINDS` SÖZLEŞMEYİ anlatıyor, bu liste
 * GERÇEĞİ. Aradaki boşluk Faz 0'da üç canlı hataya sebep olan boşluğun ta
 * kendisi — bir cins tanımlı olduğu için çalışabilir sanılıyor, çalıştırıcısı
 * yok, düğüm sessizce atlanıyor ve kullanıcı akışın çalıştığını sanıyor.
 *
 * Üç yer bunu okumak ZORUNDA:
 *   - editör paleti  → yalnız bu cinsler sürüklenebilir olsun
 *   - kaydetme doğrulaması → listede olmayan cins için UYARI (hata DEĞİL:
 *     yarın bağlanacak bir düğümü bugünden çizebilmek gerekiyor, ve API'den
 *     ya da şablondan gelen bir grafik OKUNABİLİR olmak zorunda —
 *     `TRIGGER_KINDS_WITH_ENGINE` başlığındaki aynı gerekçe)
 *   - motor → düğüm `SKIPPED` + gerekçe, koşu kaydında görünür
 *
 * ---------------------------------------------------------------------------
 * ✅ GRAFİK YÜRÜTÜCÜSÜ ARTIK VAR (Faz 2)
 * ---------------------------------------------------------------------------
 * Burada bir süre "grafik yürüten hiçbir çağıran bugün YOK" yazıyordu; artık
 * yanlış. `@mentorivo/automation-core` → `planGraphRun` düğümleri topolojik
 * sırada geziyor ve kuru çalıştırma ile zamanlanmış koşu ONU çağırıyor —
 * doğrusal kurallar dahil (`graphFromLegacy` ile açılarak). Listenin anlamı
 * değişmedi ama daralmadı da: bu cinsleri motor GERÇEKTEN çalıştırıyor,
 * listede olmayanları `GRAPH_NODE_NOT_IMPLEMENTED` ile görünür şekilde geçiyor.
 *
 * ---------------------------------------------------------------------------
 * Neden bu ikisi DIŞARIDA
 * ---------------------------------------------------------------------------
 *   `trigger.webhook`→ Faz 5. Uç, imza doğrulaması ve sır saklama hattı yok.
 *   `action.http`    → Faz 5. Giden istek katmanı, zaman aşımı ve SSRF
 *                      korumasının çalışma zamanı yarısı (DNS çözümünden sonra
 *                      `isBlockedHttpHost`in ikinci kez çağrılması) yazılmadı.
 *
 * `trigger.manual` İÇERİDE ve bu bir istisna değil: elle çalıştırma ucu bugün
 * zaten var (`POST /automations/:id/run`). Grafik için yeni bir şey gerekmiyor,
 * yalnız akışın giriş noktası olarak adlandırılıyor.
 *
 * Aksiyon cinsleri `ACTION_TYPES_WITH_EXECUTOR`ten TÜRETİLİYOR. Elle
 * yazılsaydı Faz 5 üç kelime aksiyonunu o listeye taşıdığında burası eksik
 * kalırdı ve bu dosya sözleşme ile gerçeği ayırmak için var olduğu hâlde
 * kendisi yanlış gerçeği anlatırdı.
 */
export const GRAPH_NODE_KINDS_WITH_ENGINE: readonly GraphNodeKind[] = [
  'trigger.schedule',
  'trigger.metric',
  /// Faz 3 — `WorkflowEventsService` olayı alıp eşleşen kuralları çalıştırıyor.
  'trigger.event',
  'trigger.manual',
  'scope',
  'filter',
  /**
   * Faz 3 — `planGraphRun` `wait`e gelince o dalı KESİYOR ve bir DEVAM
   * (`continuation`) döndürüyor; koşuyu askıya alıp gecikmeli işi kuran taraf
   * çağıran (worker). Motor saf kalıyor: kuyruğu o görmüyor, yalnız "bu dal
   * şu ana kadar bekleyecek, elinde şu hedefler vardı" diyor.
   *
   * Listeye girmesinin anlamı şu: düğüm artık `GRAPH_NODE_NOT_IMPLEMENTED` ile
   * ATLANMIYOR — yani altındaki aksiyonlar beklemeden çalışmıyor.
   */
  'wait',
  /// Faz 2 — `planGraphRun` düşen kümeyi de taşıyor: `branch` GEÇENLERİ `true`,
  /// GEÇMEYENLERİ `false` portuna koyuyor. Bir süre burada değildi ve o zaman
  /// doğruydu (motor yalnız geçen kümeyi ilerletiyordu); artık `false` dalının
  /// da uygulayıcısı var.
  'branch',
  /**
   * Faz 5 — gelen webhook.
   *
   * `POST /automations/hooks/:token` kuralı GERÇEKTEN çalıştırıyor
   * (`webhook.controller.ts` → `RunsService.execute`), yani tetikleyicinin
   * "motoru" var. Tetikleyici düğümü zaten motorun YÜRÜTTÜĞÜ bir şey değil —
   * akışın giriş noktası; `trigger.manual` ile aynı gerekçe.
   *
   * ⚠️ Bir eksiği kaldı ve listeye girmesini engellemiyor: HMAC doğrulaması
   * `main.ts` ham gövdeyi saklamadığı için `WEBHOOK_RAW_BODY_MISSING` veriyor.
   * İmza İSTEĞE BAĞLI bir katman; token'ın kendisi zaten sır ve tek başına
   * çalışıyor. Sessiz değil — ayrı bir kodla söyleniyor.
   */
  'trigger.webhook',
  ...ACTION_TYPES_WITH_EXECUTOR.map((type): GraphActionKind => `action.${type}`),
  /**
   * Faz 5 — giden dış çağrı.
   *
   * İki ayağı da bağlandı: `planGraphRun` düğümü PLANLIYOR (koşu başına tek
   * satır, `notify` ile aynı gerekçe — hedefi bir kampanya değil dışarıdaki
   * bir uç) ve `action-executor.service.ts` `HTTP_ACTION_LOG_TYPE` dalıyla
   * UYGULUYOR.
   *
   * 🔴 Listeye ancak İKİSİ birden hazır olunca girdi. Uygulayıcı Faz 5'te
   * yazılmıştı ama motor düğümü atlıyordu; o aralıkta listeye eklemek,
   * editörde sürüklenebilen ve koşuda hiçbir şey yapmayan bir kutu demekti —
   * bu dosyanın var olma sebebinin tam tersi.
   */
  GRAPH_HTTP_ACTION_KIND,
];

const ENGINE_KIND_SET: ReadonlySet<string> = new Set<string>(GRAPH_NODE_KINDS_WITH_ENGINE);

/// Küme araması — çağıranlar `GRAPH_NODE_KINDS_WITH_ENGINE.includes(...)` ile
/// her düğümde diziyi taramasın. Grafik başına 40 düğüm × her tuş vuruşu,
/// editörde ölçülebilir bir fark.
export function graphNodeKindHasEngine(kind: string): boolean {
  return ENGINE_KIND_SET.has(kind);
}
